import type {DiffLineType} from '@github-ui/diffs/types'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {UndoIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {type RefObject, useCallback, useEffect, useMemo, useRef} from 'react'

import {DialogStateProvider} from '../../contexts/DialogStateContext'
import type {GenericMarkerNavEl} from '../../helpers/marker-navigator'
import {useMarkerNavigation} from '../../hooks/use-marker-navigation'
import type {CommentAuthor, DiffAnnotation, Thread} from '../../conversation-types'
import {DiffAnnotationLevels} from '../../conversation-types'
import {InlineAnnotation} from './InlineAnnotation'
import styles from './InlineMarkers.module.css'
import type {InlineReviewThreadProps} from './InlineReviewThread'
import {InlineReviewThread} from './InlineReviewThread'
import {useDialogModeActions, useDialogModeIsEnabled} from '../../stores/selectors'
import {useSelectedMarkerId} from '@github-ui/pull-requests/pull-request-store-selectors'

import type {DiffLine, LineKey} from '../../types'
import {useEnterDialogMode} from '../../hooks/use-enter-dialog-mode'
import type {MarkersStatusAction} from '../../hooks/use-markers-dialog-status'
import {useSuggestedChanges} from '../../hooks/use-suggested-changes'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

// Define annotation levels priority for sorting
const annotationLevelPriority = {
  [DiffAnnotationLevels.Failure]: 0,
  [DiffAnnotationLevels.Warning]: 1,
  [DiffAnnotationLevels.Notice]: 2,
}

export interface InlineMarkersProps extends Pick<InlineReviewThreadProps, 'batchingEnabled' | 'viewerData'> {
  annotations: DiffAnnotation[]
  cellRef: RefObject<HTMLTableCellElement | null>
  dispatchMarkersStatus: React.Dispatch<MarkersStatusAction>
  conversationListThreads: Thread[]
  fileAnchor?: string
  filePath: string
  gridCellId: string
  lineMarkersKey: LineKey
  gutterSizeOffset: string
  inlineMarkersRef: RefObject<HTMLDivElement | null>
  isRowSelected: boolean
  diffLine: DiffLine
  lineType: DiffLineType
  onCloseConversationList: () => void
  onCloseFocusMode: () => void
  onDeleteLineComment: () => void
  shouldListenForEscapeKey?: boolean
  shouldStartNewConversationWithSuggestedChange?: boolean
  threadsConnectionId?: string
  ghostUser?: CommentAuthor
  children?: React.ReactNode
}

export function InlineMarkers(props: InlineMarkersProps) {
  return (
    <DialogStateProvider>
      <InlineMarkersInternal {...props} />
    </DialogStateProvider>
  )
}

function InlineMarkersInternal({
  annotations,
  cellRef,
  diffLine,
  dispatchMarkersStatus,
  children,
  conversationListThreads,
  inlineMarkersRef,
  isRowSelected,
  lineMarkersKey,
  fileAnchor,
  filePath,
  gridCellId,
  gutterSizeOffset,
  lineType,
  onCloseConversationList,
  onCloseFocusMode,
  onDeleteLineComment,
  threadsConnectionId,
  shouldStartNewConversationWithSuggestedChange,
  ghostUser,
  shouldListenForEscapeKey,
  ...rest
}: InlineMarkersProps) {
  const isInDialogMode = useDialogModeIsEnabled(gridCellId)
  const {disableDialogMode, enableDialogMode} = useDialogModeActions()
  const enterDialogMode = useEnterDialogMode(gridCellId, cellRef, dispatchMarkersStatus)
  const optimizedDiffFlagEnabled = useFeatureFlag('pr_simple_diffs')
  const suggestedChangesConfig = useSuggestedChanges(diffLine, shouldStartNewConversationWithSuggestedChange)
  // Create a ref for the markers container
  const markersContainerRef = useRef<HTMLDivElement>(null)

  // Sort annotations by level: Failure > Warning > Notice

  const sortedAnnotations = useMemo(() => {
    return [...annotations].sort((a, b) => {
      return annotationLevelPriority[a.annotationLevel] - annotationLevelPriority[b.annotationLevel]
    })
  }, [annotations])

  const markers: Array<Thread | GenericMarkerNavEl> = useMemo(() => {
    if (children) {
      return [...conversationListThreads, {id: 'new-comment'} as GenericMarkerNavEl, ...sortedAnnotations]
    }

    return [...conversationListThreads, ...sortedAnnotations]
  }, [children, conversationListThreads, sortedAnnotations])

  // prevent events bubbling up to the parent elements (e.g Cell, DiffFileHeader, etc...)
  // Note: we do need the bubble up the onKeyDown event, since that exits dialog mode from the cell
  const handleStopPropagation = (event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) =>
    event.stopPropagation()

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    const eventTargetNode = e.target as Node
    const eventTargetElement = e.target as HTMLElement
    const closestGridCell = eventTargetElement.closest(`[data-grid-cell-id=${gridCellId}]`) as HTMLElement | undefined
    // Handle escape press on the row
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (shouldListenForEscapeKey && closestGridCell?.contains(eventTargetNode) && e?.key === 'Escape') {
      onCloseFocusMode()
      disableDialogMode()
      closestGridCell?.focus()
      return
    }
    //when the user has the final element in the focus loop focused and hits tab, we want to wrap them back
    //around to the beginning and focus the first element in the loop, which is the collapse button
    if (
      closestGridCell?.contains(eventTargetNode) &&
      eventTargetElement.getAttribute('data-exit-dialog-mode-button') === 'true' &&
      !e?.shiftKey &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      e?.key === 'Tab'
    ) {
      //prevent the default behavior and instead wrap around to focus the first collapse button
      e.preventDefault()
      ;(closestGridCell?.querySelector('[data-is-first-collapse-button="true"]') as HTMLElement)?.focus()
      return
    }

    //when the user has the first element in the focus loop focused and hits shift+tab, we want to wrap them back
    //around to the end and focus the last element in the loop, which is the exit dialog button
    if (
      closestGridCell?.contains(eventTargetNode) &&
      eventTargetElement.getAttribute('data-is-first-collapse-button') === 'true' &&
      e?.shiftKey &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      e?.key === 'Tab'
    ) {
      //prevent the default behavior and instead wrap around to focus the close button
      e.preventDefault()
      ;(closestGridCell?.querySelector('[data-exit-dialog-mode-button="true"]') as HTMLElement)?.focus()
      return
    }
  }

  const handleOnFocus = (event: React.FocusEvent) => {
    handleStopPropagation(event)
    if (!isInDialogMode) {
      enableDialogMode(gridCellId)
    }
  }

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (!e.target) return
      if (!isInDialogMode) return

      const targetIsNotInsideInlineMarkersDialog = !(e.target as HTMLElement).closest('[data-inline-markers]')

      if (targetIsNotInsideInlineMarkersDialog) {
        disableDialogMode()
      }
    },
    [disableDialogMode, isInDialogMode],
  )

  useEffect(() => {
    ssrSafeDocument?.addEventListener('mousedown', handleClickOutside)
    return () => ssrSafeDocument?.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  const selectedMarkerId = useSelectedMarkerId()
  const selectedMarkerIdRef = useRef<string | null>(null)

  /**
   * If navigating to a review comment or annotation, ensure the marker is visible
   * by entering dialog mode. This handles both initial page load with a hash
   * and navigation from other UI elements like ThreadPreview.
   */
  useEffect(() => {
    if (selectedMarkerId && selectedMarkerIdRef.current !== selectedMarkerId) {
      const threadIds = conversationListThreads.map(l => l.id)
      const annotationsIds = annotations.map(l => l.id)

      if (threadIds.includes(selectedMarkerId) || annotationsIds.includes(selectedMarkerId)) {
        selectedMarkerIdRef.current = selectedMarkerId
        enterDialogMode({shouldFocusFirstMarker: false}, selectedMarkerId)
      }
    }
  }, [selectedMarkerId, enterDialogMode, conversationListThreads, annotations])

  // Use our marker navigation hook
  useMarkerNavigation({
    containerRef: markersContainerRef,
    markers,
    disabled: !isInDialogMode, // Only enable marker navigation when in dialog mode
  })

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx('d-flex pt-1', isInDialogMode ? styles.markersDialogActive : '')}
      style={{marginRight: `-${gutterSizeOffset}`}}
      aria-hidden={isInDialogMode ? 'false' : 'true'}
      onFocus={handleOnFocus}
      onClick={handleStopPropagation}
      onKeyDown={handleOnKeyDown}
      onKeyUp={handleStopPropagation}
      onMouseDown={handleStopPropagation}
      onMouseUp={handleStopPropagation}
      data-inline-markers
      ref={inlineMarkersRef}
    >
      <div className={styles.markersWrapper} ref={markersContainerRef}>
        {conversationListThreads.map((thread, i) => (
          <div
            key={`review-thread-${thread.id}`}
            className={clsx(
              'mt-1 border rounded-2 color-border-default color-shadow-small',
              i === markers.length - 1 ? 'mb-1' : 'mb-2',
            )}
            data-first-marker={i === 0}
            data-marker-id={`${thread.id}`} // Add data-marker-id attribute for keyboard navigation
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={isInDialogMode ? 0 : -1} // Make the marker focusable
          >
            <InlineReviewThread
              threadPositionNumber={i + 1}
              isFirstThread={i === 0}
              filePath={filePath}
              lineMarkersKey={lineMarkersKey}
              fileAnchor={fileAnchor}
              gridCellId={gridCellId}
              onDeleteLineComment={onDeleteLineComment}
              threadId={thread.id}
              threadsConnectionId={threadsConnectionId}
              suggestedChangesConfig={suggestedChangesConfig}
              ghostUser={ghostUser}
              {...rest}
            />
          </div>
        ))}
        {children}
        {sortedAnnotations.map((annotation, i) => {
          const isFirstMarker = i === 0 && annotation === markers[0]
          return (
            <InlineAnnotation
              key={`annotation-${annotation.id}`}
              annotation={annotation}
              gridCellId={gridCellId}
              isFirstMarker={isFirstMarker}
            />
          )
        })}
      </div>
      {isInDialogMode && (
        <IconButton
          icon={UndoIcon}
          className={clsx(
            styles.closeMarkersDialogButton,
            'ml-2 position-relative',
            conversationListThreads.length === 0 ? 'mt-2' : 'mt-1',
          )}
          aria-label="Return to code"
          data-exit-dialog-mode-button={'true'}
          variant="default"
          size="small"
          // Using onMouseUp instead of onClick to custom handle onKeyDown events
          onMouseUp={() => {
            onCloseFocusMode()
            disableDialogMode()
            if (optimizedDiffFlagEnabled) {
              cellRef.current?.focus()
            }
          }}
          onKeyDown={e => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (e.key === 'Enter' || e.code === 'Space') {
              onCloseFocusMode()
              disableDialogMode()
              if (optimizedDiffFlagEnabled) {
                cellRef.current?.focus()
              }
            }
          }}
        />
      )}
    </div>
  )
}
